import { Maps } from '@greystar/designsystem';
import {
  Marker,
  PROPERTY_MAP_FALLBACK_IMAGE_URL,
  extractPropertyId,
  getPdpUrlById,
  useAnalytics,
  getDictionaryEntry,
  PROPERTY_CARD_NO_PRICE_FIRST_TEXT,
  PROPERTY_CARD_NO_PRICE_SECOND_TEXT,
} from '@greystar/lib';
import { BaseProductWithPriceOptions, SearchMapProps } from '@greystar/ui/src/component-props';
import { useEffect, useMemo, useRef } from 'react';
import { useI18n } from 'next-localization';

/**
 * Transforms an array of products into an array of markers for use on a map.
 *
 * @param {BaseProduct[]} products - The array of products to transform.
 * @returns {Marker[]} An array of markers with latitude, longitude, title, and id.
 *
 * @remarks
 * - Filters products to include only those with latitude and longitude in their additional fields.
 * - Extracts latitude and longitude from the additional fields of each product.
 * - Extracts title from the `ec_brand` field of each product.
 * - Extracts id from the `ec_item_group_id` field of each product.
 */

const transformMarkers = (products: BaseProductWithPriceOptions[]): Marker[] => {
  const markers: Marker[] = [];

  products
    .filter(
      (product) => product.additionalFields['latitude'] && product.additionalFields['longitude']
    )
    .forEach((product) => {
      // bedrooms is accessed from the additionalFields object
      // price can be accessed directly from the product object (ec_price)
      // image can be accessed directly from the product object (ec_image)

      markers.push({
        url: getPdpUrlById(
          extractPropertyId(product.clickUri),
          product.ec_brand ?? '',
          product.additionalFields['city'] as string,
          product.additionalFields['state'] as string
        ),
        lat: product.additionalFields['latitude'] as number,
        lng: product.additionalFields['longitude'] as number,
        price: product.priceOptions.find((option) => option.type === 'tmlp')?.price ?? '',
        baseRent: product.priceOptions.find((option) => option.type === 'baseRent')?.price ?? '',
        ...{
          noPriceFirstText: PROPERTY_CARD_NO_PRICE_FIRST_TEXT,
          noPriceSecondText: PROPERTY_CARD_NO_PRICE_SECOND_TEXT,
        },
        id:
          product.ec_item_group_id ??
          `${product.additionalFields['latitude']}_${product.additionalFields['longitude']}`,
        image: product.ec_thumbnails[0]
          ? product.ec_thumbnails[0] + '?qlt=100&wid=180&fit=constrain'
          : PROPERTY_MAP_FALLBACK_IMAGE_URL,
        title: product.ec_brand ?? '',
        bedroom: '',
        priceText: 'From',
      });
    });

  return markers;
};

export const SearchMap = ({ properties, ...props }: SearchMapProps): JSX.Element => {
  const markers = useMemo(() => transformMarkers(properties), [properties]);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef('map');
  const analytics = useAnalytics();
  const { t } = useI18n();

  const pushDataLayerEvent = (
    event?: string,
    additionalData: Partial<{
      actionType: string;
      clickText: string;
      property_id: string;
      property_name: string;
      destination_url: string;
      event: string;
    }> = {}
  ) => {
    const eventData = {
      event: event ?? 'map_view',
      action_type: additionalData.actionType,
      state: props.location?.split(', ')[1],
      property_location: props.location?.trim(),
      property_id: additionalData.property_id ?? '',
      property_name: additionalData.property_name ?? '',
      module_name: 'search results map view',
      inpage_location: 'middle',
      click_text: additionalData.clickText,
      click_type: 'internal',
      destination_url: additionalData.destination_url ?? '',
      search_term: props.location?.trim(),
      price_range_filter: '',
      property_type_filter: '',
      property_features_filter: '',
      timeline_filter: '',
    };
    analytics.trackEvent(event ?? 'map_view', eventData);
  };

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    // Wait for the community card to render before firing analytics
    const waitForElement = (
      selector: string,
      attempts = 10,
      delay = 100
    ): Promise<Element | null> =>
      new Promise((resolve) => {
        const check = (remaining: number) => {
          const el = document.querySelector(selector);
          if (el || remaining <= 0) return resolve(el);
          setTimeout(() => check(remaining - 1), delay);
        };
        check(attempts);
      });

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const markerPin = target.closest('.maps-embed--marker-pin');

      if (markerPin) {
        const markerWrapper = markerPin.closest('.yNHHyP-marker-view');
        if (markerWrapper) {
          const price = markerPin.textContent?.trim() || '';

          waitForElement('a.communitycard--map').then((renderedCard) => {
            const heading =
              renderedCard?.querySelector('.communitycard--map--heading')?.textContent?.trim() ??
              'unknown';
            const href = renderedCard?.getAttribute('href') ?? '';
            const match = href.match(/\/p_(\d+)/);
            const property_id = match ? match[1] : '';

            pushDataLayerEvent('map_view_marker_click', {
              clickText: price,
              property_id: property_id,
              property_name: heading,
              actionType: 'marker click',
              destination_url: href,
            });
          });
        }
        return;
      }

      const communityCardLink = target.closest('a.communitycard--map');
      if (communityCardLink) {
        const heading =
          communityCardLink.querySelector('.communitycard--map--heading')?.textContent?.trim() ??
          '';
        const url = communityCardLink.getAttribute('href') ?? '';
        const match = url.match(/\/p_(\d+)/);
        const property_id = match ? match[1] : '';

        pushDataLayerEvent('property_details_entry', {
          clickText: heading,
          property_id: property_id,
          property_name: heading,
          destination_url: url,
          actionType: 'property card click',
        });
        return;
      }

      const expandButton = target.closest('.maps-expand--button button');
      if (expandButton) {
        const isExpanded = expandButton.getAttribute('aria-label') === 'Back to List';
        const actionType = isExpanded ? 'collapse' : 'expand';
        pushDataLayerEvent('map_view', {
          clickText: actionType === 'expand' ? '<-' : '<- Back to List',
          actionType: actionType,
        });
        return;
      }

      const mapControlButton = target.closest('.maps-controls button');
      if (mapControlButton) {
        const buttons = Array.from(document.querySelectorAll('.maps-controls button'));
        const buttonIndex = buttons.indexOf(mapControlButton);

        switch (buttonIndex) {
          case 0:
            viewRef.current = viewRef.current === 'satellite' ? 'map' : 'satellite';
            pushDataLayerEvent('map_view', {
              clickText: '',
              actionType: `view changed - ${viewRef.current}`,
            });
            break;
          case 1:
            pushDataLayerEvent('map_view', {
              clickText: '+',
              actionType: 'zoom in',
            });
            break;
          case 2:
            pushDataLayerEvent('map_view', {
              clickText: '-',
              actionType: 'zoom out',
            });
            break;
        }
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [props.view, props.location, properties]);

  return (
    <div ref={mapContainerRef}>
      <Maps
        setView={props.setView}
        view={props.view}
        apiKey={process.env.NEXT_PUBLIC_GoogleApiKey ?? ''}
        mapID={process.env.NEXT_PUBLIC_GoogleMapID ?? ''}
        zoom={props.initialZoom}
        forceZoom={properties.length === 1}
        mapCenter={{ lat: props.centerLat, lng: props.centerLng }}
        bounds={
          props.bounds
            ? {
                north: props.bounds.ne.lat,
                east: props.bounds.ne.lng,
                south: props.bounds.sw.lat,
                west: props.bounds.sw.lng,
                padding: 0,
              }
            : undefined
        }
        markers={markers}
        onMapEvent={props.onMapEvent}
        onMarkerEvent={props.onMarkerEvent}
        onZoomEvent={props.onMapEvent}
        mapEmbedClassName="maps-search"
        expandButtonLabel={getDictionaryEntry(t('map-expand'), 'Expand Map')}
        recenterButtonLabel={getDictionaryEntry(t('map-recenter'), 'Recenter Map')}
        zoomInButtonLabel={getDictionaryEntry(t('map-zoom-in'), 'Zoom In Map')}
        zoomOutButtonLabel={getDictionaryEntry(t('map-zoom-out'), 'Zoom Out Map')}
      />
    </div>
  );
};
